
'use client';

import Link from 'next/link';
import { Menu, LogOut, LayoutGrid, Users, FilePlus, Handshake, Bell, Search, Globe, LogIn, Home, Compass, Briefcase, FileText, MessageSquareWarning, ShieldCheck, Trash2, Scroll, Scale, FileArchive, User, Pencil, FastForward, Info, Copy, ExternalLink, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuGroup,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/doi-tac-module/hooks/use-toast';
import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { cn } from '@/doi-tac-module/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Image from 'next/image';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { useIsMobile } from '@/doi-tac-module/hooks/use-mobile';
import { NotificationDropdown } from '@/doi-tac-module/components/notification-dropdown';
import { AgencyQrDialog } from '@/doi-tac-module/components/agency-qr-dialog';

const Logo = ({ className }: { className?: string }) => (
    <Image src="/img/HJPNG.png" alt="HelloJob Logo" width={120} height={40} className={cn("h-10 w-auto", className)} priority />
);

const AgencyMobileSecondaryHeader = ({ showNav, pathname, agencyMenu }: { showNav: boolean; pathname: string; agencyMenu: { href: string; label: string }[] }) => {
    return (
        <div className={cn(
            "md:hidden sticky top-16 z-30 w-full bg-background/95 backdrop-blur-sm border-b transition-transform duration-300",
            !showNav && "-translate-y-full"
        )}>
            <div className="w-full overflow-x-auto whitespace-nowrap no-scrollbar">
                <div className="flex items-center h-14 px-2">
                    {agencyMenu.map((item) => {
                        const isActive = (item.href !== '/doi-tac' && pathname.startsWith(item.href)) || (item.href === '/doi-tac' && pathname === '/doi-tac');
                        return (
                            <Link
                                href={item.href}
                                key={item.href}
                                className={cn(
                                    "flex items-center justify-center text-sm transition-colors flex-shrink-0 w-auto px-4 py-2 rounded-md",
                                    isActive ? 'text-[#F2B92A] font-bold bg-[#F2B92A]/10' : 'text-muted-foreground hover:text-[#F2B92A]'
                                )}
                            >
                                <span className="text-center leading-tight">{item.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export function AgencyHeader() {
    const { user, logout, isLoggedIn, role, isApprovedAgent } = useAuth();
    const { language } = useLanguage();
    const { toast } = useToast();
    const [isScrolled, setIsScrolled] = useState(false);
    const [lastScrollY, setLastScrollY] = useState(0);
    const [showNav, setShowNav] = useState(true);
    const [isClient, setIsClient] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const pathname = usePathname();
    const router = useRouter();
    const isMobile = useIsMobile();

    const t = agencyTranslations.header;

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: agencyTranslations.header.avatarMenu.copied[language],
            description: label,
            className: "bg-[#0D8DC8] text-white border-none",
        });
    };

    const ShareLinksContent = () => (
        <div className="space-y-4 p-4 bg-white rounded-2xl">
            <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500">{agencyTranslations.header.avatarMenu.publicLink[language]}</p>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 group hover:border-[#F2B92A] transition-all overflow-hidden">
                    <span className="text-[11px] text-slate-500 truncate flex-grow min-w-0">
                        {`${isClient ? window.location.origin : ''}/cac-dai-ly/${user?.uid}`}
                    </span>
                    <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 rounded-lg hover:bg-[#F2B92A]/10 hover:text-[#F2B92A]"
                        onClick={() => copyToClipboard(`${window.location.origin}/cac-dai-ly/${user?.uid}`, agencyTranslations.header.avatarMenu.publicLink[language])}
                    >
                        <Copy className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <div className="space-y-2">
                <p className="text-xs font-bold text-slate-500">{agencyTranslations.header.avatarMenu.internalLink[language]}</p>
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 group hover:border-[#0D8DC8] transition-all overflow-hidden">
                    <span className="text-[11px] text-slate-500 truncate flex-grow min-w-0">
                        {`${isClient ? window.location.origin : ''}/doi-tac/partner/${user?.uid}`}
                    </span>
                    <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-8 w-8 p-0 rounded-lg hover:bg-[#0D8DC8]/10 hover:text-[#0D8DC8]"
                        onClick={() => copyToClipboard(`${window.location.origin}/doi-tac/partner/${user?.uid}`, agencyTranslations.header.avatarMenu.internalLink[language])}
                    >
                        <Copy className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (!isClient || !isMobile) {
            setShowNav(true);
            return;
        }
        const controlNavbar = () => {
            if (window.scrollY > lastScrollY && window.scrollY > 80) {
                setShowNav(false);
            } else {
                setShowNav(true);
            }
            setLastScrollY(window.scrollY);
        };
        window.addEventListener('scroll', controlNavbar);
        return () => window.removeEventListener('scroll', controlNavbar);
    }, [isClient, isMobile, lastScrollY]);

    const agencyMenu = [
        { href: '/doi-tac', label: t.home[language] },
        { href: '/doi-tac/quy-trinh', label: t.roadmap[language] },
        { href: '/doi-tac/viec-lam', label: t.jobs[language] },
        { href: '/doi-tac/ung-vien', label: t.candidates[language] },
    ];

    const getDisplayAvatar = (user: any) => {
        if (!user) return undefined;
        const agency = user.agencyProfile;

        // Ưu tiên 1: Ảnh người đại diện (representativePhoto)
        if (agency?.representativePhoto) {
            return typeof agency.representativePhoto === 'string' 
                ? agency.representativePhoto 
                : agency.representativePhoto.url;
        }

        // Ưu tiên 2: Logo Công ty (companyLogo)
        if (agency?.companyLogo) {
            return typeof agency.companyLogo === 'string'
                ? agency.companyLogo
                : agency.companyLogo.url;
        }

        // Ưu tiên 3: Ảnh mặc định (urlAvatar)
        return user.urlAvatar || undefined;
    };

    const getDisplayName = (user: any) => {
        if (!user) return "";
        
        // Ưu tiên 1: Họ tên Giám đốc (MD/CEO) từ luồng cập nhật hồ sơ
        if (user.agencyProfile?.managingDirector) {
            return user.agencyProfile.managingDirector;
        }
        
        // Ưu tiên 2: Tên người dùng (fullName)
        if (user.fullName && user.fullName !== 'Người dùng' && user.fullName !== 'User') {
            return user.fullName;
        }
        
        // Fallback: Thông báo chưa điền tên
        const translations: any = {
            vi: "Bạn chưa điền tên",
            bn: "আপনি নাম পূরণ করেননি"
        };
        return translations[language] || "Name not provided";
    };

    const getDisplayRole = (user: any, currentPath: string, lang: string) => {
        if (!user) return "";
        const role = user.role;
        const isPartnerPath = currentPath.startsWith('/doi-tac');
        
        const labels: any = {
            vi: {
                admin: "Quản trị viên (Admin)",
                master: "Đối tác chiến lược (Tổng đại lý)",
                newMaster: "Đối tác chiến lược mới",
                ctv: "Đối tác phát triển (Đại lý cấp 1)",
                newCtv: "Đối tác phát triển mới",
                newEmployer: "Doanh nghiệp mới",
                activeEmployer: "Doanh nghiệp tuyển dụng"
            },
            bn: {
                admin: "প্রশাসক (Admin)",
                master: "কৌশলগত অংশীদার (মাস্টার এজেন্ট)",
                newMaster: "নতুন কৌশলগত অংশীদার",
                ctv: "বাজার উন্নয়ন অংশীদার (এজেন্ট লেভেল ১)",
                newCtv: "নতুন বাজার উন্নয়ন অংশীদার",
                newEmployer: "নতুন নিয়োগকর্তা",
                activeEmployer: "নিয়োগকর্তা কোম্পানি"
            }
        };
        
        const t = labels[lang] || labels.vi;
        
        const profile = user.agencyProfile;
        const hasInfo = profile && (
            profile.companyName || profile.officialEmail || profile.managingDirector || 
            profile.representativePhoto || profile.companyLogo || profile.officeAddress
        );

        // Luồng Đối tác
        if (isPartnerPath) {
            if (role === 'ADMIN') return t.admin;
            if (role === 'MASTER_CTV') return hasInfo ? t.master : t.newMaster;
            if (role === 'CTV') return hasInfo ? t.ctv : t.newCtv;
            return ""; 
        }
        
        // Luồng Trang chủ / Tuyển dụng
        const employerRoles = ['company', 'ADMIN', 'MASTER_CTV', 'CTV'];
        if (employerRoles.includes(role)) {
            return hasInfo ? t.activeEmployer : t.newEmployer;
        }
        
        return ""; 
    };

    if (!isClient) return null;

    return (
        <div className={cn(
            "sticky top-0 z-50 w-full transition-transform duration-300",
            isClient && !showNav && isMobile && "-translate-y-full"
        )}>
            <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
                    <Link href="/doi-tac" className="flex items-center gap-2">
                        <Logo />
                    </Link>

                    <nav className="hidden md:flex items-center gap-6 text-sm">
                        {agencyMenu.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    'transition-colors hover:text-[#F2B92A] py-2 font-medium',
                                    pathname === link.href ? 'text-[#F2B92A] font-bold' : 'text-foreground/80'
                                )}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        {isLoggedIn ? (
                            <div className="flex items-center gap-4">
                                {isApprovedAgent && <AgencyQrDialog agencyId={user?.agencyProfile?.code || user?.uid || 'default-agency'} />}
                                {isApprovedAgent && <NotificationDropdown />}
                                
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B92A] focus-visible:ring-offset-2">
                                            <Avatar className="h-10 w-10 cursor-pointer transition-all duration-300 hover:scale-110 hover:ring-2 hover:ring-[#F2B92A] hover:ring-offset-2 border border-slate-100">
                                                <AvatarImage src={getDisplayAvatar(user)} alt="User Avatar" />
                                                <AvatarFallback className="bg-[#0D8DC8] text-white">
                                                    {user?.fullName?.charAt(0).toUpperCase() || 'A'}
                                                </AvatarFallback>
                                            </Avatar>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-[280px] p-2 rounded-2xl shadow-2xl border-none">
                                        <div className="px-3 py-2 border-b border-slate-50 mb-1">
                                            <p className="text-[10px] text-muted-foreground font-normal uppercase tracking-wider">{t.avatarMenu.myAccount[language]}</p>
                                            <p className="text-sm font-bold text-slate-900 truncate">{user?.fullName || 'Đối tác'}</p>
                                        </div>
                                        <DropdownMenuItem asChild className="p-0 mb-1 focus:bg-transparent">
                                            <Link 
                                                href={`/cac-dai-ly/${user?.uid}`}
                                                className={cn(
                                                    "cursor-pointer flex items-center w-full p-3 transition-all rounded-xl",
                                                    pathname === `/cac-dai-ly/${user?.uid}` ? "bg-[#0D8DC8]/10 border-l-4 border-[#0D8DC8] rounded-l-none" : "hover:bg-slate-50"
                                                )}
                                            >
                                                <div className={cn("p-2 rounded-lg mr-3", pathname === `/cac-dai-ly/${user?.uid}` ? "bg-[#0D8DC8]/20" : "bg-slate-100")}>
                                                    <User className="h-5 w-5 text-[#0D8DC8]" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className={cn("font-bold text-sm text-slate-900", pathname === `/cac-dai-ly/${user?.uid}` && "text-[#0D8DC8]")}>{agencyTranslations.header.avatarMenu.viewPublic[language]}</span>
                                                    <span className="text-[10px] text-muted-foreground line-clamp-1">{agencyTranslations.header.avatarMenu.viewPublicDesc[language]}</span>
                                                </div>
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild className="p-0 mb-1 focus:bg-transparent">
                                            <Link 
                                                href="/doi-tac/ho-so"
                                                className={cn(
                                                    "cursor-pointer flex items-center w-full p-3 transition-all rounded-xl",
                                                    pathname === '/doi-tac/ho-so' ? "bg-slate-200/50 border-l-4 border-slate-400 rounded-l-none" : "hover:bg-slate-50"
                                                )}
                                            >
                                                <div className={cn("p-2 rounded-lg mr-3", pathname === '/doi-tac/ho-so' ? "bg-slate-300" : "bg-slate-100")}>
                                                    <Info className="h-5 w-5 text-slate-600" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className={cn("font-bold text-sm text-slate-900", pathname === '/doi-tac/ho-so' && "text-slate-900")}>{agencyTranslations.header.avatarMenu.viewInternal[language]}</span>
                                                    <span className="text-[10px] text-muted-foreground line-clamp-1">{agencyTranslations.header.avatarMenu.viewInternalDesc[language]}</span>
                                                </div>
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild className="p-0 mb-1 focus:bg-transparent">
                                            <Link 
                                                href="/doi-tac/cap-nhat-ho-so"
                                                className={cn(
                                                    "cursor-pointer flex items-center w-full p-3 transition-all rounded-xl",
                                                    pathname === '/doi-tac/cap-nhat-ho-so' ? "bg-[#F2B92A]/10 border-l-4 border-[#F2B92A] rounded-l-none" : "hover:bg-slate-50"
                                                )}
                                            >
                                                <div className={cn("p-2 rounded-lg mr-3", pathname === '/doi-tac/cap-nhat-ho-so' ? "bg-[#F2B92A]/20" : "bg-slate-100")}>
                                                    <Pencil className="h-5 w-5 text-[#F2B92A]" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className={cn("font-bold text-sm text-slate-900", pathname === '/doi-tac/cap-nhat-ho-so' && "text-[#D9A321]")}>{agencyTranslations.header.avatarMenu.editProfile[language]}</span>
                                                    <span className="text-[10px] text-muted-foreground line-clamp-1">{agencyTranslations.header.avatarMenu.editProfileDesc[language]}</span>
                                                </div>
                                            </Link>
                                        </DropdownMenuItem>
                                        
                                        <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
                                            <DialogTrigger asChild>
                                                <DropdownMenuItem
                                                    className="p-3 cursor-pointer transition-all rounded-xl hover:bg-slate-50 focus:bg-transparent group"
                                                    onSelect={(e) => { e.preventDefault(); setIsShareDialogOpen(true); }}
                                                >
                                                    <div className="p-2 rounded-lg mr-3 bg-slate-100 group-hover:bg-slate-200 transition-colors">
                                                        <FastForward className="h-5 w-5 text-slate-600" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-sm text-slate-900">{agencyTranslations.header.avatarMenu.shareProfile[language]}</span>
                                                        <span className="text-[10px] text-muted-foreground line-clamp-1">{agencyTranslations.header.avatarMenu.shareProfileDesc[language]}</span>
                                                    </div>
                                                </DropdownMenuItem>
                                            </DialogTrigger>
                                            <DialogContent className="sm:max-w-md rounded-3xl border-none shadow-2xl p-0 overflow-hidden">
                                                <DialogHeader className="p-6 bg-gradient-to-br from-[#0D8DC8]/10 to-[#F2B92A]/10 border-b border-slate-50">
                                                    <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
                                                        <FastForward className="h-6 w-6 text-[#0D8DC8]" />
                                                        {agencyTranslations.header.avatarMenu.shareDialogTitle[language]}
                                                    </DialogTitle>
                                                </DialogHeader>
                                                <ShareLinksContent />
                                            </DialogContent>
                                        </Dialog>
                                    </DropdownMenuContent>
                                </DropdownMenu>

                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="rounded-full hover:bg-slate-100 transition-colors group">
                                            <LayoutGrid className="h-6 w-6 text-slate-600 group-hover:scale-110 transition-transform" />
                                            <span className="sr-only">Open Menu</span>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-[360px] p-0 shadow-2xl border-slate-200 rounded-xl overflow-hidden" align="end" sideOffset={8}>
                                        <div className="p-4 bg-slate-50/50 border-b border-slate-100">
                                            <div className="flex items-center gap-3">
                                                <Link href="/doi-tac/ho-so">
                                                    <Avatar className="h-12 w-12 border-2 border-white shadow-sm hover:border-[#F2B92A] transition-all cursor-pointer">
                                                        <AvatarImage src={getDisplayAvatar(user)} />
                                                        <AvatarFallback className="bg-[#0D8DC8] text-white">
                                                            {user?.fullName?.charAt(0).toUpperCase() || 'A'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                </Link>
                                                <div className="flex flex-col">
                                                    <p className="text-base font-bold text-slate-900 leading-none">{getDisplayName(user)}</p>
                                                    <p className="text-xs text-slate-500 mt-1 font-medium">{getDisplayRole(user, pathname, language)}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <DropdownMenuGroup className="p-2">
                                            <div className="grid grid-cols-3 gap-1">
                                                {[
                                                    { href: '/doi-tac', label: t.home[language], icon: Home },
                                                    { href: '/doi-tac/quy-trinh', label: t.roadmap[language], icon: Compass },
                                                    { href: '/doi-tac/viec-lam', label: t.jobs[language], icon: Briefcase },
                                                    { href: '/doi-tac/ung-vien', label: t.candidates[language], icon: Users },
                                                    { href: '/doi-tac/tai-lieu-chia-se', label: t.shareDocuments[language], icon: FileArchive },
                                                    { href: '/doi-tac/trash', label: t.trash[language], icon: Trash2 },
                                                    { href: '/bang-dieu-khien', label: 'Dữ liệu', icon: FileText },
                                                    { href: '/gop-y', label: 'Góp ý', icon: MessageSquareWarning },
                                                    { href: '/dieu-khoan-chinh-sach', label: 'Điều khoản', icon: Scroll },
                                                    { href: '/chinh-sach-bao-mat', label: 'Quyền riêng tư', icon: ShieldCheck },
                                                    { href: '/giai-quyet-tranh-chap', label: 'Tranh chấp', icon: Scale },
                                                ].map((link) => (
                                                    <DropdownMenuItem asChild key={link.href}>
                                                        <Link
                                                            href={link.href}
                                                            className="flex flex-col items-center justify-center p-3 h-24 hover:bg-slate-50 rounded-xl cursor-pointer transition-all group"
                                                        >
                                                            <div className="h-10 w-10 bg-slate-50 group-hover:bg-white rounded-full flex items-center justify-center mb-2 transition-colors border border-transparent group-hover:border-slate-100 group-hover:shadow-sm">
                                                                <link.icon className="h-5 w-5 text-slate-600 group-hover:text-[#0D8DC8]" />
                                                            </div>
                                                            <span className="text-[11px] font-bold text-center leading-tight text-slate-600 group-hover:text-slate-900">
                                                                {link.label}
                                                            </span>
                                                        </Link>
                                                    </DropdownMenuItem>
                                                ))}
                                            </div>
                                        </DropdownMenuGroup>

                                        <DropdownMenuSeparator className="bg-slate-100" />

                                        <div className="p-2 bg-slate-50/50 space-y-1">
                                            {/* Security Item */}
                                            <DropdownMenuItem asChild>
                                                <Link
                                                    href="/doi-tac/security"
                                                    className="flex items-center gap-3 px-4 py-3 hover:bg-white rounded-xl cursor-pointer transition-all group border border-transparent hover:border-slate-100 hover:shadow-sm"
                                                >
                                                    <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center transition-colors border border-slate-100 shadow-sm shrink-0">
                                                        <ShieldCheck className="h-5 w-5 text-slate-600 group-hover:text-[#0D8DC8] transition-colors" />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-slate-700 group-hover:text-slate-900 leading-tight">
                                                            {agencyTranslations.security.menuLabel[language]}
                                                        </span>
                                                        <span className="text-[10px] text-slate-500 mt-0.5">
                                                            {agencyTranslations.security.dashboard.healthStatus[language]}
                                                        </span>
                                                    </div>
                                                </Link>
                                            </DropdownMenuItem>

                                            {/* Logout Item */}
                                            <DropdownMenuItem 
                                                className="flex items-center gap-3 px-4 py-3 hover:bg-red-50/50 rounded-xl cursor-pointer transition-all group border border-transparent hover:border-red-100 hover:shadow-sm focus:bg-red-50/50 focus:outline-none"
                                                onClick={() => { logout(); router.push('/doi-tac'); }}
                                            >
                                                <div className="h-10 w-10 bg-white rounded-full flex items-center justify-center transition-colors border border-red-50 shadow-sm group-hover:border-red-100 shrink-0">
                                                    <LogOut className="h-5 w-5 text-red-500" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-bold text-red-600 leading-tight">
                                                        {t.logout[language]}
                                                    </span>
                                                    <span className="text-[10px] text-red-400 mt-0.5">
                                                        {t.logoutDesc[language]}
                                                    </span>
                                                </div>
                                            </DropdownMenuItem>
                                        </div>

                                        <div className="p-3 bg-white border-t border-slate-50 text-center">
                                            <p className="text-[10px] text-slate-400 font-medium tracking-tight">Partner Portal • v2.0</p>
                                        </div>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ) : (
                            <Button asChild className="bg-[#F2B92A] hover:bg-[#F2B92A]/90">
                                <Link href={`/doi-tac/ung-vien`}>{t.login[language]}</Link>
                            </Button>
                        )}
                    </div>

                    {isMobile && (
                        <div className="flex items-center gap-2">
                            {isLoggedIn ? (
                                <>
                                    {isApprovedAgent && <AgencyQrDialog agencyId={user?.agencyProfile?.code || user?.uid || 'default-agency'} />}
                                    {isApprovedAgent && <NotificationDropdown />}
                                    
                                    <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
                                        <DialogTrigger asChild>
                                            <button className="rounded-full ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F2B92A] focus-visible:ring-offset-2">
                                                <Avatar className="h-9 w-9 cursor-pointer transition-all duration-300 border border-slate-200">
                                                    <AvatarImage src={getDisplayAvatar(user)} alt="User Avatar" />
                                                    <AvatarFallback className="bg-[#0D8DC8] text-white text-xs">
                                                        {user?.fullName?.charAt(0).toUpperCase() || 'A'}
                                                    </AvatarFallback>
                                                </Avatar>
                                            </button>
                                        </DialogTrigger>
                                        <DialogContent className="sm:max-w-md rounded-3xl p-0 overflow-hidden border-none shadow-2xl mx-4">
                                            <div className="bg-gradient-to-br from-[#0D8DC8]/10 to-[#F2B92A]/10 p-6 border-b border-slate-50 text-center flex flex-col items-center">
                                                <Avatar className="h-20 w-20 border-4 border-white shadow-xl mb-4">
                                                    <AvatarImage src={getDisplayAvatar(user)} alt="User Avatar" />
                                                    <AvatarFallback className="bg-[#0D8DC8] text-white text-2xl">
                                                        {user?.fullName?.charAt(0).toUpperCase() || 'A'}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="space-y-1">
                                                    <h3 className="text-xl font-bold text-slate-900">{getDisplayName(user)}</h3>
                                                    <p className="text-xs text-muted-foreground font-semibold flex items-center justify-center gap-1.5 bg-white/80 backdrop-blur px-3 py-1 rounded-full shadow-sm w-fit mx-auto">
                                                        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                                                        {getDisplayRole(user, pathname, language)}
                                                    </p>
                                                </div>
                                            </div>
                                            
                                            <div className="grid gap-3 p-6 bg-white overflow-y-auto max-h-[60vh]">
                                                <Button 
                                                    asChild 
                                                    variant="outline" 
                                                    className="h-auto py-4 px-4 justify-start gap-4 border-2 hover:border-[#0D8DC8] hover:bg-[#0D8DC8]/5 transition-all rounded-2xl group border-slate-50 shadow-sm"
                                                    onClick={() => setIsProfileDialogOpen(false)}
                                                >
                                                    <Link href={`/cac-dai-ly/${user?.uid}`} className="flex items-center w-full text-left">
                                                        <div className="bg-[#0D8DC8]/10 p-3 rounded-xl group-hover:bg-[#0D8DC8]/20 transition-colors">
                                                            <User className="h-6 w-6 text-[#0D8DC8]" />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-base text-slate-900">{agencyTranslations.header.avatarMenu.viewPublic[language]}</span>
                                                            <span className="text-xs text-muted-foreground">{agencyTranslations.header.avatarMenu.viewPublicDesc[language]}</span>
                                                        </div>
                                                    </Link>
                                                </Button>

                                                <Button 
                                                    asChild 
                                                    variant="outline" 
                                                    className="h-auto py-4 px-4 justify-start gap-4 border-2 hover:border-slate-400 hover:bg-slate-50 transition-all rounded-2xl group border-slate-50 shadow-sm"
                                                    onClick={() => setIsProfileDialogOpen(false)}
                                                >
                                                    <Link href="/doi-tac/ho-so" className="flex items-center w-full text-left">
                                                        <div className="bg-slate-100 p-3 rounded-xl group-hover:bg-slate-200 transition-colors">
                                                            <Info className="h-6 w-6 text-slate-600" />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-base text-slate-900">{agencyTranslations.header.avatarMenu.viewInternal[language]}</span>
                                                            <span className="text-xs text-muted-foreground">{agencyTranslations.header.avatarMenu.viewInternalDesc[language]}</span>
                                                        </div>
                                                    </Link>
                                                </Button>

                                                <Button 
                                                    asChild 
                                                    variant="outline" 
                                                    className="h-auto py-4 px-4 justify-start gap-4 border-2 hover:border-[#F2B92A] hover:bg-[#F2B92A]/5 transition-all rounded-2xl group border-slate-50 shadow-sm"
                                                    onClick={() => setIsProfileDialogOpen(false)}
                                                >
                                                    <Link href="/doi-tac/cap-nhat-ho-so" className="flex items-center w-full text-left">
                                                        <div className="bg-[#F2B92A]/10 p-3 rounded-xl group-hover:bg-[#F2B92A]/20 transition-colors">
                                                            <Pencil className="h-6 w-6 text-[#F2B92A]" />
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="font-bold text-base text-slate-900">{agencyTranslations.header.avatarMenu.editProfile[language]}</span>
                                                            <span className="text-xs text-muted-foreground">{agencyTranslations.header.avatarMenu.editProfileDesc[language]}</span>
                                                        </div>
                                                    </Link>
                                                </Button>

                                                <div className="pt-4 border-t border-slate-50 mt-2">
                                                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 px-1">{agencyTranslations.header.avatarMenu.shareProfile[language]}</p>
                                                    <ShareLinksContent />
                                                </div>
                                            </div>
                                        </DialogContent>
                                    </Dialog>

                                    <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                                        <SheetTrigger asChild>
                                            <Button variant="ghost" size="icon" className="text-slate-600 group">
                                                <Menu className="group-hover:scale-110 transition-transform h-6 w-6" />
                                            </Button>
                                        </SheetTrigger>
                                        <SheetContent side="right" className="flex flex-col p-0 border-l-slate-100">
                                            <SheetHeader className="p-4 border-b border-slate-50">
                                                <SheetTitle>
                                                    <Link href="/doi-tac" onClick={() => setIsMobileMenuOpen(false)}>
                                                        <Logo />
                                                    </Link>
                                                </SheetTitle>
                                            </SheetHeader>
                                            
                                            {isLoggedIn ? (
                                                <>
                                                    <div className="p-4">
                                                        <Link href="/doi-tac/ho-so" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                                                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 shadow-sm">
                                                                <Avatar className="h-12 w-12 border-2 border-white shadow-sm">
                                                                    <AvatarImage src={getDisplayAvatar(user)} />
                                                                    <AvatarFallback className="bg-[#0D8DC8] text-white">
                                                                        {user?.fullName?.charAt(0).toUpperCase() || 'A'}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                                <div className="flex flex-col">
                                                                    <p className="text-base font-bold text-slate-900 leading-none">{getDisplayName(user)}</p>
                                                                    <p className="text-[10px] text-slate-500 mt-1 font-semibold">{getDisplayRole(user, pathname, language)}</p>
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    </div>

                                                    <DropdownMenuSeparator className="bg-slate-100" />

                                                    <div className="p-2 flex-grow overflow-y-auto no-scrollbar">
                                                        <div className="grid grid-cols-3 gap-2">
                                                            {[
                                                                { href: '/doi-tac', label: t.home[language], icon: Home },
                                                                { href: '/doi-tac/quy-trinh', label: t.roadmap[language], icon: Compass },
                                                                { href: '/doi-tac/viec-lam', label: t.jobs[language], icon: Briefcase },
                                                                { href: '/doi-tac/ung-vien', label: t.candidates[language], icon: Users },
                                                                { href: '/doi-tac/tai-lieu-chia-se', label: t.shareDocuments[language], icon: FileArchive },
                                                                { href: '/doi-tac/trash', label: t.trash[language], icon: Trash2 },
                                                                { href: '/bang-dieu-khien', label: 'Dữ liệu', icon: FileText },
                                                                { href: '/gop-y', label: 'Góp ý', icon: MessageSquareWarning },
                                                                { href: '/doi-tac/security', label: 'Bảo mật', icon: ShieldCheck },
                                                                { href: '/dieu-khoan-chinh-sach', label: 'Điều khoản', icon: Scroll },
                                                                { href: '/chinh-sach-bao-mat', label: 'Quyền riêng tư', icon: ShieldCheck },
                                                                { href: '/giai-quyet-tranh-chap', label: 'Tranh chấp', icon: Scale },
                                                            ].map((link) => {
                                                                const isActive = pathname === link.href;
                                                                return (
                                                                    <Link
                                                                        key={link.href}
                                                                        href={link.href}
                                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                                        className={cn(
                                                                            "flex flex-col items-center justify-center p-3 h-24 rounded-xl transition-all border",
                                                                            isActive 
                                                                                ? "bg-[#F2B92A]/5 border-[#F2B92A]/20 text-[#F2B92A]" 
                                                                                : "bg-slate-50/50 border-slate-100 text-slate-600 active:bg-slate-100"
                                                                        )}
                                                                    >
                                                                        <div className={cn(
                                                                            "h-10 w-10 rounded-full flex items-center justify-center mb-2 transition-colors",
                                                                            isActive ? "bg-white shadow-sm" : "bg-white border border-slate-50 shadow-sm"
                                                                        )}>
                                                                            <link.icon className={cn("h-5 w-5", isActive ? "text-[#F2B92A]" : "text-slate-600")} />
                                                                        </div>
                                                                        <span className={cn(
                                                                            "text-[10px] font-bold text-center leading-tight px-1",
                                                                            isActive ? "text-[#F2B92A]" : "text-slate-600"
                                                                        )}>
                                                                            {link.label}
                                                                        </span>
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    <DropdownMenuSeparator className="bg-slate-100" />

                                                    <div className="p-4 mt-auto">
                                                        <Button
                                                            variant="ghost"
                                                            className="w-full justify-center text-red-500 hover:text-red-600 hover:bg-red-50 font-bold rounded-xl h-12 gap-3 border border-red-100"
                                                            onClick={() => { logout(); router.push('/doi-tac'); setIsMobileMenuOpen(false); }}
                                                        >
                                                            <LogOut className="h-5 w-5" />
                                                            {t.logout[language]}
                                                        </Button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="p-6 flex flex-col items-center justify-center h-full text-center space-y-6">
                                                    <div className="h-20 w-20 bg-slate-50 rounded-full flex items-center justify-center border border-slate-100">
                                                        <LogIn className="h-10 w-10 text-slate-400" />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <h3 className="text-lg font-bold text-slate-900">{t.login[language]}</h3>
                                                        <p className="text-sm text-slate-500 px-4">Đăng nhập để quản lý công việc của đối tác hiệu quả hơn.</p>
                                                    </div>
                                                    <Button asChild className="w-full bg-[#F2B92A] hover:bg-[#F2B92A]/90 h-12 rounded-xl text-sm font-medium shadow-lg" onClick={() => setIsMobileMenuOpen(false)}>
                                                        <Link href="/doi-tac/ung-vien">Đăng nhập ngay</Link>
                                                    </Button>
                                                </div>
                                            )}
                                        </SheetContent>
                                    </Sheet>
                                </>
                            ) : (
                                <Button asChild size="sm" className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 h-9 px-4 rounded-xl text-xs font-medium">
                                    <Link href="/doi-tac/ung-vien">{t.login[language]}</Link>
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </header>
            {isMobile && <AgencyMobileSecondaryHeader showNav={showNav} pathname={pathname} agencyMenu={agencyMenu} />}
        </div>
    );
}
